/**
 * Pure domain logic for the data-analytics dashboards — single source of truth
 * for category selection, month→date-range conversion, trigger matching, and
 * the difference / remark rules. No API or React here (unit-tested in __tests__).
 */

/**
 * Coerce a value to a finite number.
 *
 * @param {*} val  a number, numeric string, or anything else
 * @returns {number|null} the number, or null when `val` is empty or non-numeric
 */
const toNum = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const n = typeof val === "number" ? val : parseFloat(val);
  return Number.isFinite(n) ? n : null;
};

/**
 * Days in the month for a `YYYY-MM` string (day 0 of the next month).
 *
 * @param {string} month  `YYYY-MM`
 * @returns {number|null} the day count, or null when no month is given
 */
export const getDaysInMonth = (month) => {
  if (!month) return null;
  const [year, m] = month.split("-");
  return new Date(year, m, 0).getDate();
};

/**
 * Convert a selected `YYYY-MM` month into an inclusive `{ fromDate, toDate }`
 * pair (`YYYY-MM-DD`) spanning the whole month — the range passed to analytics.
 *
 * @param {string} selectedMonth  `YYYY-MM`
 * @returns {{fromDate: (string|null), toDate: (string|null)}} the month's bounds,
 *   both null when no month is given
 */
export const monthToDateRange = (selectedMonth) => {
  if (!selectedMonth) return { fromDate: null, toDate: null };
  const [year, m] = selectedMonth.split("-");
  const days = getDaysInMonth(selectedMonth);
  if (!days) return { fromDate: null, toDate: null };
  const mm = String(m).padStart(2, "0");
  return {
    fromDate: `${year}-${mm}-01`,
    toDate: `${year}-${mm}-${String(days).padStart(2, "0")}`,
  };
};

/**
 * Build the ordered category list for a tab: keep only questions whose
 * `questionId` is in `questionIds`, in list order, name taken from the API
 * `title`. Questions not returned by the API for the FY are skipped.
 *
 * @param {Array} questions  reporting questions from the API, each `{ questionId, title }`
 * @param {number[]} questionIds  the tab's question ids, in display order
 * @returns {Array<{questionId: number, name: string}>} one entry per id the API
 *   returned, named by its title or `KPI <id>` when the title is missing
 */
export const getCategories = (questions, questionIds) => {
  const byId = new Map();
  (questions || []).forEach((q) => {
    if (q && q.questionId != null) byId.set(Number(q.questionId), q);
  });
  return (questionIds || [])
    .map(Number)
    .filter((id) => byId.has(id))
    .map((id) => {
      const q = byId.get(id);
      return { questionId: id, name: q.title || `KPI ${id}` };
    });
};

/**
 * Derive the previous financial year's id from its `financial_year_value`
 * (`"2026-2027"` → `"2025-2026"`) by looking it up in the FY list.
 *
 * @param {Array} fyList  every financial year, each `{ id, financial_year_value }`
 * @param {Object} fyObj  the selected financial year
 * @returns {number|null} the prior FY's id, or null when it isn't in the list
 */
export const previousFinancialYearId = (fyList, fyObj) => {
  if (!fyObj || !fyObj.financial_year_value) return null;
  const [start, end] = fyObj.financial_year_value.split("-").map(Number);
  if (!start || !end) return null;
  const prevValue = `${start - 1}-${end - 1}`;
  const prev = (fyList || []).find(
    (f) => f.financial_year_value === prevValue
  );
  return prev && prev.id != null ? prev.id : null;
};

/**
 * Stable key for a reporting scope — a source plus an optional sub-location, so
 * KMC-T1 (`"5:9"`), KMC-T2 (`"5:10"`) and the source as a whole (`"5:"`) never
 * collide. Single source of truth for keying `byLocation` and its lookups.
 *
 * @param {number|string} sourceId  the source (location) id
 * @param {number|string|null} [subLocationId]  the sub-location id, or null/undefined
 *   for the source as a whole
 * @returns {string} `"<sourceId>:<subLocationId>"`, the sub-location part empty
 *   for a source-level scope
 */
export const scopeKey = (sourceId, subLocationId) =>
  `${Number(sourceId)}:${
    subLocationId === null || subLocationId === undefined
      ? ""
      : Number(subLocationId)
  }`;

/**
 * The `locationIds` value analytics expects for one scope: a bare source id for
 * a source-level scope, `sub_<id>` for a sub-location — the two forms the
 * backend's location filter understands. Requesting `sub_<id>` keeps a
 * sub-location's rows from being widened back out to its whole source.
 *
 * @param {{id: number, subLocationId: (number|null)}} location  the scope to request
 * @returns {string|number} `sub_<subLocationId>` for a sub-location scope, the
 *   numeric source id otherwise
 */
export const analyticsLocationParam = (location) =>
  location && location.subLocationId != null
    ? `sub_${Number(location.subLocationId)}`
    : Number(location.id);

/**
 * The trigger row for a question + scope whose period covers `[fromDate,toDate]`
 * (the selected month).
 *
 * A sub-location scope prefers its own trigger and falls back to one set on the
 * source as a whole (`subLocationId` null), which applies to every sub-location
 * under it. A source-level scope only ever uses a source-wide trigger — one
 * sub-location's limit is not the site's limit.
 *
 * @param {Array} triggers  every trigger row for the FY, each
 *   `{ questionId, sourceId, subLocationId, fromDate, toDate, minTargetData, maxTargetData }`
 * @param {number} questionId  the question the targets belong to
 * @param {number} sourceId  the scope's source (location) id
 * @param {string} fromDate  start of the month, `YYYY-MM-DD`; omit to ignore the period
 * @param {string} toDate  end of the month, `YYYY-MM-DD`; omit to ignore the period
 * @param {number|null} [subLocationId=null]  the scope's sub-location, null for
 *   the source as a whole
 * @returns {{min: *, max: *}} the matched targets, both null when no trigger covers
 *   the month for this scope
 */
export const triggerForMonth = (
  triggers,
  questionId,
  sourceId,
  fromDate,
  toDate,
  subLocationId = null
) => {
  /**
   * Whether a trigger's own period spans the whole selected month. With no month
   * bounds given every trigger counts as covering.
   *
   * @param {{fromDate: string, toDate: string}} t  a trigger row
   * @returns {boolean} true when the trigger covers `[fromDate, toDate]`
   */
  const covers = (t) =>
    !fromDate || !toDate || (t.fromDate <= fromDate && t.toDate >= toDate);

  const candidates = (triggers || []).filter(
    (t) =>
      Number(t.questionId) === Number(questionId) &&
      Number(t.sourceId) === Number(sourceId) &&
      covers(t)
  );

  const sourceWide = candidates.find((t) => t.subLocationId == null);
  const exact =
    subLocationId == null
      ? null
      : candidates.find(
          (t) =>
            t.subLocationId != null &&
            Number(t.subLocationId) === Number(subLocationId)
        );

  const match = exact || sourceWide;
  if (!match) return { min: null, max: null };
  return {
    min: match.minTargetData ?? null,
    max: match.maxTargetData ?? null,
  };
};

/**
 * `Actual − Max` — how far a reading sits over (positive) or under its limit.
 *
 * @param {*} actual  the reported value
 * @param {*} max  the maximum target
 * @returns {number|string} the difference, or "" when either side is non-numeric
 *   (nothing to compare)
 */
export const computeDifference = (actual, max) => {
  const a = toNum(actual);
  const m = toNum(max);
  if (a === null || m === null) return "";
  return a - m;
};

/**
 * Remark for a row: "Within Limit" when there is no positive breach (or the max
 * limit is 0), "Exceeding Limit" when Actual is over Max.
 *
 * @param {*} actual  the reported value
 * @param {*} max  the maximum target
 * @returns {string} "Within Limit", "Exceeding Limit", or "" when the two are
 *   incomparable
 */
export const computeRemark = (actual, max) => {
  const diff = computeDifference(actual, max);
  if (diff === "") return "";
  if (toNum(max) === 0) return "Within Limit";
  return diff <= 0 ? "Within Limit" : "Exceeding Limit";
};

/**
 * Trim trailing decimal zeros for display, rounding to two decimals.
 *
 * @param {*} val  a number or numeric string
 * @returns {string|*} the trimmed number as a string, or `val` unchanged when it
 *   is empty or non-numeric
 */
export const formatNum = (val) => {
  if (val === null || val === undefined || val === "") return val;
  const num = typeof val === "number" ? val : parseFloat(val);
  if (isNaN(num)) return val;
  return parseFloat(num.toFixed(2)).toString();
};
